import { config } from '@terminal/config';
import { useRemoteStorage } from '@terminal/hooks/use-remote-storage/useRemoteStorage';
import { apiFetch } from '@terminal/utils/api-fetch/apiFetch';
import { create } from '@terminal/utils/store/createStore';
import { isWithinInterval } from 'date-fns/isWithinInterval';
import type { PostHog } from 'posthog-js';

export enum SupportedLanguages {
  EN = 'en',
  JA = 'ja',
  PT = 'pt',
  KO = 'ko',
}

interface FlagsLanguageEnabled {
  enabled: boolean;
}

export interface FlagsMaintenance {
  from: string;
  to: string;
}

export type FlagsLanguage = {
  [key in SupportedLanguages]?: FlagsLanguageEnabled;
};

export interface SystemFlags {
  maintenance?: FlagsMaintenance;
  languages?: FlagsLanguage;
  feature_web: FeatureFlag[];
}

const flagsEndpoint = `system/flags/v1`;

export const FEATURE_FLAGS = [
  'sample',
  'hide_alfred_download_button',
  'alfred_user_upload',
  'live_candles_feature_flag',
  'scheduled_task_feature',
  'snbc_user',
  'msf_connection',
  'enable_chartv2',
  'theme_intraday_chart',
  'inline_citations_feature_flag',
  'alfred_ask_analyst',
  'REASON-1901-provenance-bundle-download',
  'PLATFORM-4342-korean_language',
  'REASON-2022-alfred-settings-page',
  'DATA-1542-price-history-v2',
  'onboarding_build_with_alfred',
  'REASON-2103-alfred-mentions',
  'PLATFORM-4671-ao-earnings',
  'PLATFORM-4767-screener-exclusion',
  'KG-786-Macro_Financial_Theme',
  'PLATFORM-4744-theme-earnings',
  'KG-840-api_entity-performance-v7',
  'KG-733-TO_Pulse',
  'KG-976-basket-summary-v2',
  'KG-976-earnings-filter-v2',
  'enable_event_alerts_v2',
  'REASON-2327-teams-connection',
  'REASON-2419-copy-paste-insights',
  'PLATFORM-4949-watchlist-notifications',
  'REASON-2424-insights-page-copy',
  'KG-997-show-related-themes-section',
  'REASON-2467-earnings-insights-v4',
] as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[number];

const featureFlagSet = new Set<string>(FEATURE_FLAGS);

const isFeatureFlag = (value: string): value is FeatureFlag =>
  featureFlagSet.has(value);

interface SystemFlagsStore {
  systemFlags: Omit<SystemFlags, 'feature_web'>;
  featureFlags: FeatureFlag[];
  posthogClient?: PostHog;
  initialise(posthogClient?: PostHog): Promise<void>;
  checkIsInMaintenance(): boolean;
  getFeatureFlagState: (flag: FeatureFlag) => boolean;
}
const configFlags = config.app.featureFlags.filter(isFeatureFlag);
export const useFlags = create<SystemFlagsStore>((set, get) => ({
  systemFlags: {},
  getFeatureFlagState: (flag: FeatureFlag) => get().featureFlags.includes(flag),
  featureFlags: configFlags,
  initialise: async posthogClient => {
    const systemFlags = await apiFetch<SystemFlags>(flagsEndpoint);

    if (posthogClient) {
      set({
        posthogClient,
      });

      posthogClient.onFeatureFlags(() => {
        const posthogFlags = posthogClient.featureFlags.getFlagsWithDetails();

        set(state => {
          const posthogFlagKeys = new Set(Object.keys(posthogFlags));
          const enabledFlags = Object.entries(posthogFlags)
            .filter(([_, details]) => details.enabled)
            .map(([key]) => key)
            .filter(isFeatureFlag);

          const nonPosthogFlags = state.featureFlags.filter(
            flag => !posthogFlagKeys.has(flag)
          );

          return {
            featureFlags: [...nonPosthogFlags, ...enabledFlags],
          };
        });
      });

      const flagsWithDetails = posthogClient.featureFlags.getFlagsWithDetails();

      // Flags may be empty after re-authentication; reload if needed
      if (!flagsWithDetails || Object.keys(flagsWithDetails).length === 0) {
        posthogClient.reloadFeatureFlags();
      }
    }

    if (!systemFlags.error) {
      const { feature_web: featureWeb, ...rest } = systemFlags.data;

      const remoteStorageFeatureFlags =
        useRemoteStorage.getState().items?.feature_flags ?? {};
      const remoteStorageFlags = Object.entries(remoteStorageFeatureFlags)
        .filter(([_, value]) => value === 'true')
        .map(([key]) => key)
        .filter(isFeatureFlag);

      set(state => ({
        systemFlags: rest,
        featureFlags: [
          ...new Set([
            ...featureWeb,
            ...remoteStorageFlags,
            ...state.featureFlags,
          ]),
        ],
      }));
    }
  },
  checkIsInMaintenance: () => {
    const {
      systemFlags: { maintenance },
    } = get();

    if (maintenance) {
      const start = new Date(maintenance.from);
      const end = new Date(maintenance.to);
      return isWithinInterval(new Date(), {
        start,
        end,
      });
    }

    return false;
  },
}));
